import {
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Paper,
  Avatar,
  Chip,
  Typography,
  Divider,
  CircularProgress,
  Tooltip,
  ClickAwayListener,
  styled,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  LocationOnOutlined as LocationOnIcon,
  PhoneOutlined as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  WorkOutline as WorkIcon,
  ChevronRight as ChevronRightIcon,
  SearchOff as SearchOffIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { topBarHeight } from "app/utils/constant";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import useAuth from "app/hooks/useAuth";
import { VIEW_PROSPECT, VIEW_REFERENCE } from "Permissions";

const MAX_DISPLAY_RESULTS = 40;

// STYLED COMPONENTS
const SearchContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 120,
  width: "100%",
  display: "flex",
  alignItems: "center",
  height: topBarHeight,
  padding: "0 16px",
  backgroundColor:
    theme.palette.mode === "dark" || theme.palette.type === "dark"
      ? theme.palette.background.paper
      : "#ffffff",
  color: theme.palette.text.primary,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease-in-out",
  borderBottom: `1px solid ${theme.palette.divider || "rgba(0, 0, 0, 0.08)"
    }`,
}));

const SearchInput = styled("input")(({ theme }) => ({
  flex: 1,
  border: "none",
  outline: "none",
  fontSize: "1rem",
  fontFamily: "inherit",
  padding: "8px 12px",
  height: "100%",
  background: "transparent",
  color: theme.palette.text.primary,
  "&::placeholder": {
    color: theme.palette.text.secondary,
    opacity: 0.8,
  },
}));

const ResultsPaper = styled(Paper)(({ theme }) => {
  const isDark = theme.palette.mode === "dark" || theme.palette.type === "dark";
  return {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 16,
    right: 16,
    maxWidth: 880,
    margin: "0 auto",
    zIndex: 1300,
    borderRadius: 14,
    maxHeight: "min(540px, 75vh)",
    overflowY: "auto",
    overflowX: "hidden",
    boxShadow: isDark
      ? "0 20px 50px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.08)"
      : "0 20px 50px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(15, 23, 42, 0.08)",
    backgroundColor: isDark ? theme.palette.background.paper : "#ffffff",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: isDark
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(0, 0, 0, 0.16)",
      borderRadius: 3,
    },
  };
});

// Fast pre-compiled keyword highlighting with high contrast, WCAG AA compliance, and zero regex statefulness issues
const HighlightMatch = memo(function HighlightMatch({ text, regex }) {
  if (!text) return null;
  const str = String(text);
  if (!regex) return <>{str}</>;

  // Use stateless RegExp to avoid lastIndex bug with 'g' flag
  const matchChecker = new RegExp(regex.source, "i");
  const parts = str.split(regex);
  if (parts.length <= 1 && !matchChecker.test(str)) return <>{str}</>;

  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null;
        return matchChecker.test(part) ? (
          <Box
            key={index}
            component="mark"
            sx={{
              display: "inline",
              fontFamily: "inherit",
              fontWeight: 700,
              color: (theme) =>
                theme.palette.mode === "dark" || theme.palette.type === "dark"
                  ? "#fde047"
                  : "#78350f",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" || theme.palette.type === "dark"
                  ? "rgba(245, 158, 11, 0.35)"
                  : "#fef08a",
              boxShadow: (theme) =>
                theme.palette.mode === "dark" || theme.palette.type === "dark"
                  ? "0 0 0 1px rgba(245, 158, 11, 0.5)"
                  : "0 0 0 1px rgba(217, 119, 6, 0.3)",
              borderRadius: "3px",
              px: "3px",
              py: "1px",
            }}
          >
            {part}
          </Box>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        );
      })}
    </>
  );
});

// Memoized Result Item to prevent re-rendering all items on arrow key selection
const ResultItem = memo(function ResultItem({
  result,
  index,
  isSelected,
  regex,
  isDarkMode,
  onClick,
}) {
  const isProspect = result.IsProspect === "Yes";
  const name = result.Name || "Unnamed";
  const initial = name.trim().charAt(0).toUpperCase();
  const hasValidPicture = Boolean(
    result.Picture &&
    !result.Picture.includes("default") &&
    result.Picture.trim().length > 4
  );


  return (
    <Fragment>
      {index > 0 && (
        <Divider
          component="li"
          sx={{
            borderColor: isDarkMode
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.05)",
          }}
        />
      )}
      <ListItemButton
        selected={isSelected}
        onClick={onClick}
        sx={{
          px: 2.5,
          py: 1.5,
          transition: "all 0.15s ease",
          borderLeft: isSelected
            ? isDarkMode
              ? "3.5px solid #90caf9"
              : "3.5px solid #1976d2"
            : "3.5px solid transparent",
          bgcolor: isSelected
            ? isDarkMode
              ? "rgba(144, 202, 249, 0.12)"
              : "rgba(25, 118, 210, 0.08)"
            : "transparent",
          "&:hover": {
            bgcolor: isDarkMode
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(25, 118, 210, 0.04)",
          },
          "&.Mui-selected": {
            bgcolor: isDarkMode
              ? "rgba(144, 202, 249, 0.12)"
              : "rgba(25, 118, 210, 0.08)",
            "&:hover": {
              bgcolor: isDarkMode
                ? "rgba(144, 202, 249, 0.18)"
                : "rgba(25, 118, 210, 0.12)",
            },
          },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 54 }}>
          <Avatar
            src={
              hasValidPicture
                ? `${"https://backend.dahiyainsurance.com"}/Upload/profile/${result.Picture}`
                : undefined
            }
            alt={name}
            sx={{
              width: 42,
              height: 42,
              fontWeight: 700,
              fontSize: "1.05rem",
              bgcolor: isProspect
                ? isDarkMode
                  ? "rgba(34, 197, 94, 0.2)"
                  : "#e8f5e9"
                : isDarkMode
                  ? "rgba(59, 130, 246, 0.2)"
                  : "#e3f2fd",
              color: isProspect
                ? isDarkMode
                  ? "#4ade80"
                  : "#15803d"
                : isDarkMode
                  ? "#93c5fd"
                  : "#1d4ed8",
              border: isProspect
                ? isDarkMode
                  ? "1.5px solid rgba(74, 222, 128, 0.35)"
                  : "1.5px solid rgba(34, 197, 94, 0.25)"
                : isDarkMode
                  ? "1.5px solid rgba(147, 197, 253, 0.35)"
                  : "1.5px solid rgba(25, 118, 210, 0.25)",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
            }}
          >
            {initial}
          </Avatar>
        </ListItemAvatar>

        <ListItemText
          primary={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 0.6,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 650,
                  fontSize: "0.98rem",
                  color: isDarkMode ? "#f8fafc" : "#0f172a",
                  lineHeight: 1.3,
                  letterSpacing: "0.01em",
                }}
              >
                <HighlightMatch text={name} regex={regex} />
              </Typography>

              <Chip
                label={isProspect ? "Prospect" : "Reference"}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  bgcolor: isProspect
                    ? isDarkMode
                      ? "rgba(34, 197, 94, 0.2)"
                      : "#e8f5e9"
                    : isDarkMode
                      ? "rgba(59, 130, 246, 0.2)"
                      : "#e3f2fd",
                  color: isProspect
                    ? isDarkMode
                      ? "#4ade80"
                      : "#166534"
                    : isDarkMode
                      ? "#93c5fd"
                      : "#1e40af",
                  border: isProspect
                    ? isDarkMode
                      ? "1px solid rgba(74, 222, 128, 0.3)"
                      : "1px solid rgba(34, 197, 94, 0.25)"
                    : isDarkMode
                      ? "1px solid rgba(147, 197, 253, 0.3)"
                      : "1px solid rgba(25, 118, 210, 0.25)",
                  "& .MuiChip-label": { px: 1 },
                }}
              />

              {result.SrNo && (
                <Chip
                  label={
                    <Box
                      component="span"
                      sx={{ display: "inline-flex", alignItems: "center" }}
                    >
                      <Box component="span" sx={{ opacity: 0.7, mr: 0.3 }}>
                        {"REFERENCE/"}
                      </Box>
                      <HighlightMatch text={String(result.SrNo)} regex={regex} />
                    </Box>
                  }
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    fontFamily: "monospace",
                    bgcolor: isDarkMode
                      ? "rgba(255, 255, 255, 0.08)"
                      : "#f1f5f9",
                    color: isDarkMode ? "#cbd5e1" : "#334155",
                    border: isDarkMode
                      ? "1px solid rgba(255, 255, 255, 0.12)"
                      : "1px solid #cbd5e1",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}

              {result.LeadStatus && (
                <Chip
                  label={result.LeadStatus}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                    bgcolor: isDarkMode
                      ? "rgba(148, 163, 184, 0.15)"
                      : "rgba(100, 116, 139, 0.1)",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    border: isDarkMode
                      ? "1px solid rgba(148, 163, 184, 0.25)"
                      : "1px solid rgba(100, 116, 139, 0.2)",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}
            </Box>
          }
          secondary={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: { xs: 1.5, sm: 2.2 },
                mt: 0.5,
              }}
            >
              {result.Village && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: isDarkMode ? "#94a3b8" : "#475569",
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      fontSize: 15,
                      color: isDarkMode ? "#64748b" : "#64748b",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      color: "inherit",
                    }}
                  >
                    <HighlightMatch text={result.Village} regex={regex} />
                  </Typography>
                </Box>
              )}

              {result.MobileNo && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: isDarkMode ? "#94a3b8" : "#475569",
                  }}
                >
                  <PhoneIcon
                    sx={{
                      fontSize: 15,
                      color: isDarkMode ? "#64748b" : "#64748b",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      letterSpacing: "0.02em",
                      color: "inherit",
                    }}
                  >
                    <HighlightMatch text={result.MobileNo} regex={regex} />
                  </Typography>
                </Box>
              )}

              {result.WhatsAppNo &&
                result.WhatsAppNo !== result.MobileNo && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: isDarkMode ? "#94a3b8" : "#475569",
                    }}
                  >
                    <WhatsAppIcon
                      sx={{
                        fontSize: 15,
                        color: "#16a34a",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 500,
                        letterSpacing: "0.02em",
                        color: "inherit",
                      }}
                    >
                      <HighlightMatch text={result.WhatsAppNo} regex={regex} />
                    </Typography>
                  </Box>
                )}

              {(result.Profession || result.WorkType) && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: isDarkMode ? "#94a3b8" : "#475569",
                  }}
                >
                  <WorkIcon
                    sx={{
                      fontSize: 15,
                      color: isDarkMode ? "#64748b" : "#64748b",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      color: "inherit",
                    }}
                  >
                    {result.Profession || result.WorkType}
                  </Typography>
                </Box>
              )}
            </Box>
          }
        />

        <ChevronRightIcon
          sx={{
            ml: 1,
            fontSize: 20,
            color: isSelected
              ? isDarkMode
                ? "#90caf9"
                : "#1976d2"
              : isDarkMode
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            transform: isSelected ? "translateX(3px)" : "none",
            transition: "all 0.15s ease",
          }}
        />
      </ListItemButton>
    </Fragment>
  );
});

export default function MatxSearchBox() {
  const { user, permissions } = useAuth();
  const hasSearchPermission = Boolean(
    user?.Type === "SuperAdmin" ||
    (Array.isArray(permissions) && (permissions.includes(VIEW_REFERENCE) || permissions.includes(VIEW_PROSPECT)))
  );

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const theme = useTheme();

  const inputRef = useRef(null);
  const listRef = useRef(null);

  const toggle = useCallback(() => {
    if (!hasSearchPermission) return;
    setOpen((prev) => !prev);
  }, [hasSearchPermission]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue("");
    setDebouncedQuery("");
    setResults([]);
    setLoading(false);
    setHasSearched(false);
    setSelectedIndex(-1);
  }, []);

  const handleClear = useCallback(() => {
    setInputValue("");
    setDebouncedQuery("");
    setResults([]);
    setLoading(false);
    setHasSearched(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  }, []);

  // Global shortcut (Ctrl + K / Cmd + K) to toggle search
  useEffect(() => {
    if (!hasSearchPermission) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasSearchPermission]);

  // Auto-focus input when search opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  // Scroll active item into view during keyboard navigation
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("div[role='button']");
      if (items[selectedIndex]) {
        items[selectedIndex].scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  // Debounce input to set debouncedQuery and execute search
  useEffect(() => {
    if (!hasSearchPermission) {
      setDebouncedQuery("");
      setResults([]);
      setLoading(false);
      setHasSearched(false);
      setSelectedIndex(-1);
      return;
    }

    const trimmed = inputValue.trim();

    if (!trimmed) {
      setDebouncedQuery("");
      setResults([]);
      setLoading(false);
      setHasSearched(false);
      setSelectedIndex(-1);
      return;
    }

    const isNumeric = !isNaN(trimmed);

    // Require at least 3 characters for general text search to prevent massive full-table scans
    if (!isNumeric && trimmed.length < 3) {
      setDebouncedQuery("");
      setResults([]);
      setLoading(false);
      setHasSearched(false);
      setSelectedIndex(-1);
      return;
    }

    setLoading(true);
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      setDebouncedQuery(trimmed);

      try {
        // If numeric and 1-2 digits, search by exact SrNo first
        if (isNumeric && trimmed.length < 3) {
          const srRes = await axiosPrivate.post(
            "SearchProspectBySrNo.php",
            { Search: trimmed },
            { signal: controller.signal }
          );
          if (
            srRes.data?.status === 1 &&
            Array.isArray(srRes.data?.Data) &&
            srRes.data.Data.length > 0
          ) {
            setResults(srRes.data.Data);
            setSelectedIndex(0);
            setHasSearched(true);
            setLoading(false);
            return;
          }
        }

        // General search by Name, SrNo, Mobile, WhatsApp, Village
        const res = await axiosPrivate.post(
          "SearchProspect.php",
          { Search: trimmed },
          { signal: controller.signal }
        );
        if (res.data?.status === 1 && Array.isArray(res.data?.Data)) {
          setResults(res.data.Data);
          setSelectedIndex(res.data.Data.length > 0 ? 0 : -1);
        } else {
          setResults([]);
          setSelectedIndex(-1);
        }
        setHasSearched(true);
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("SearchProspect error:", error);
        setResults([]);
        setSelectedIndex(-1);
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [inputValue, axiosPrivate, hasSearchPermission]);

  // Pre-compile search regex once per debounced query
  const searchRegex = useMemo(() => {
    if (!debouncedQuery || !debouncedQuery.trim()) return null;
    const terms = debouncedQuery.trim().split(/\s+/).filter(Boolean);
    if (!terms.length) return null;
    const escapedTerms = terms.map((t) =>
      t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    return new RegExp(`(${escapedTerms.join("|")})`, "gi");
  }, [debouncedQuery]);

  // Limit displayed items in DOM to prevent browser main-thread freezes
  const displayedResults = useMemo(() => {
    return results.slice(0, MAX_DISPLAY_RESULTS);
  }, [results]);

  const handleResultClick = useCallback(
    (id, type) => {
      if (!id) return;
      handleClose();
      if (type === "No") {
        navigate(`/References/ViewReference/${id}`);
      } else {
        navigate(`/Prospects/ViewProspect/${id}`);
      }
    },
    [handleClose, navigate]
  );

  const handleInputKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (displayedResults.length > 0) {
        setSelectedIndex((prev) =>
          prev < displayedResults.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (displayedResults.length > 0) {
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : displayedResults.length - 1
        );
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (displayedResults.length > 0) {
        const item =
          selectedIndex >= 0 && selectedIndex < displayedResults.length
            ? displayedResults[selectedIndex]
            : displayedResults[0];
        handleResultClick(item.UniqueID, item.IsProspect);
      }
    }
  };

  const isDarkMode =
    theme.palette.mode === "dark" || theme.palette.type === "dark";

  const trimmedInput = inputValue.trim();
  const isNumeric = !isNaN(trimmedInput) && trimmedInput.length > 0;
  const isShortText = !isNumeric && trimmedInput.length > 0 && trimmedInput.length < 3;

  if (!hasSearchPermission) {
    return null;
  }

  return (
    <Fragment>
      {!open && (
        <Tooltip title="Search (Ctrl + K)" arrow>
          <IconButton onClick={toggle} sx={{ color: "text.primary" }}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
      )}

      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <SearchContainer>
            <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: 24 }} />

            <SearchInput
              ref={inputRef}
              type="text"
              value={inputValue}
              placeholder="Search by Name, Village, Phone, or Serial No..."
              autoFocus
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
            />

            {loading && (
              <CircularProgress
                size={20}
                sx={{
                  mr: 1,
                  color: isDarkMode ? "#90caf9" : "#1976d2",
                }}
              />
            )}

            {inputValue && !loading && (
              <Tooltip title="Clear text" arrow>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{ mr: 0.5, color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1, my: 1.8 }}
            />

            <Tooltip title="Close (Esc)" arrow>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "text.primary" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>

            {/* SEARCH RESULTS DROPDOWN */}
            {open && (
              <ResultsPaper>
                {/* Header */}
                {displayedResults.length > 0 && (
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1.2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: isDarkMode
                        ? "rgba(255, 255, 255, 0.03)"
                        : "rgba(0, 0, 0, 0.02)",
                      borderBottom: `1px solid ${isDarkMode
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.06)"
                        }`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isDarkMode ? "#94a3b8" : "#475569",
                        fontSize: "0.8rem",
                      }}
                    >
                      Found {results.length}{" "}
                      {results.length === 1 ? "record" : "records"} for "
                      <Box
                        component="span"
                        sx={{
                          color: isDarkMode ? "#fde047" : "#78350f",
                          fontWeight: 700,
                        }}
                      >
                        {debouncedQuery}
                      </Box>
                      "
                    </Typography>

                    <Box
                      sx={{
                        display: { xs: "none", sm: "flex" },
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          fontSize: "0.72rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          component="kbd"
                          sx={{
                            px: 0.6,
                            py: 0.1,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            borderRadius: "3px",
                            bgcolor: isDarkMode
                              ? "rgba(255, 255, 255, 0.08)"
                              : "#f1f5f9",
                            color: isDarkMode ? "#cbd5e1" : "#475569",
                            border: isDarkMode
                              ? "1px solid rgba(255, 255, 255, 0.12)"
                              : "1px solid #cbd5e1",
                          }}
                        >
                          ↑
                        </Box>
                        <Box
                          component="kbd"
                          sx={{
                            px: 0.6,
                            py: 0.1,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            borderRadius: "3px",
                            bgcolor: isDarkMode
                              ? "rgba(255, 255, 255, 0.08)"
                              : "#f1f5f9",
                            color: isDarkMode ? "#cbd5e1" : "#475569",
                            border: isDarkMode
                              ? "1px solid rgba(255, 255, 255, 0.12)"
                              : "1px solid #cbd5e1",
                          }}
                        >
                          ↓
                        </Box>
                        navigate
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          fontSize: "0.72rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          component="kbd"
                          sx={{
                            px: 0.6,
                            py: 0.1,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            borderRadius: "3px",
                            bgcolor: isDarkMode
                              ? "rgba(255, 255, 255, 0.08)"
                              : "#f1f5f9",
                            color: isDarkMode ? "#cbd5e1" : "#475569",
                            border: isDarkMode
                              ? "1px solid rgba(255, 255, 255, 0.12)"
                              : "1px solid #cbd5e1",
                          }}
                        >
                          ↵
                        </Box>
                        view
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          fontSize: "0.72rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          component="kbd"
                          sx={{
                            px: 0.6,
                            py: 0.1,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            borderRadius: "3px",
                            bgcolor: isDarkMode
                              ? "rgba(255, 255, 255, 0.08)"
                              : "#f1f5f9",
                            color: isDarkMode ? "#cbd5e1" : "#475569",
                            border: isDarkMode
                              ? "1px solid rgba(255, 255, 255, 0.12)"
                              : "1px solid #cbd5e1",
                          }}
                        >
                          Esc
                        </Box>
                        close
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Results List */}
                {displayedResults.length > 0 && (
                  <List ref={listRef} sx={{ p: 0 }}>
                    {displayedResults.map((result, index) => (
                      <ResultItem
                        key={result.UniqueID || index}
                        result={result}
                        index={index}
                        isSelected={selectedIndex === index}
                        regex={searchRegex}
                        isDarkMode={isDarkMode}
                        onClick={() =>
                          handleResultClick(result.UniqueID, result.IsProspect)
                        }
                      />
                    ))}

                    {/* Overflow hint if more than MAX_DISPLAY_RESULTS */}
                    {results.length > MAX_DISPLAY_RESULTS && (
                      <Box
                        sx={{
                          py: 1.5,
                          px: 2.5,
                          textAlign: "center",
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.02)"
                            : "rgba(0, 0, 0, 0.02)",
                          borderTop: `1px solid ${isDarkMode
                            ? "rgba(255, 255, 255, 0.06)"
                            : "rgba(0, 0, 0, 0.06)"
                            }`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            fontWeight: 500,
                          }}
                        >
                          Showing top {MAX_DISPLAY_RESULTS} of {results.length}{" "}
                          results. Type more letters to refine your search.
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}

                {/* Loading State in Dropdown */}
                {loading && (
                  <Box
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                    }}
                  >
                    <CircularProgress
                      size={28}
                      sx={{ color: isDarkMode ? "#90caf9" : "#1976d2" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDarkMode ? "#94a3b8" : "#475569",
                        fontWeight: 500,
                      }}
                    >
                      Searching prospects and references...
                    </Typography>
                  </Box>
                )}

                {/* Empty State */}
                {!loading &&
                  hasSearched &&
                  results.length === 0 &&
                  debouncedQuery.length >= (isNumeric ? 1 : 3) && (
                    <Box
                      sx={{
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <SearchOffIcon
                        sx={{
                          fontSize: 48,
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          opacity: 0.6,
                          mb: 1.5,
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 650,
                          color: isDarkMode ? "#f8fafc" : "#0f172a",
                          fontSize: "1.05rem",
                        }}
                      >
                        No records found
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          maxWidth: 420,
                          color: isDarkMode ? "#94a3b8" : "#475569",
                          fontSize: "0.88rem",
                        }}
                      >
                        No prospects or references matched "
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: isDarkMode ? "#fde047" : "#78350f",
                            bgcolor: isDarkMode
                              ? "rgba(245, 158, 11, 0.35)"
                              : "#fef08a",
                            px: 0.6,
                            py: 0.1,
                            borderRadius: "3px",
                          }}
                        >
                          {debouncedQuery}
                        </Box>
                        ".
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1.5,
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          fontSize: "0.78rem",
                        }}
                      >
                        Try searching by Client Name, Village/City, Mobile
                        Number, or Serial Number.
                      </Typography>
                    </Box>
                  )}

                {/* Short text prompt: user typed 1 or 2 characters */}
                {!loading && isShortText && (
                  <Box
                    sx={{
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      color: isDarkMode ? "#94a3b8" : "#475569",
                    }}
                  >
                    <SearchIcon
                      sx={{
                        opacity: 0.7,
                        color: isDarkMode ? "#90caf9" : "#1976d2",
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Please type at least <strong>3 characters</strong> to
                      search by name/village, or enter a number for serial
                      search.
                    </Typography>
                  </Box>
                )}

                {/* Initial Guidance Prompt */}
                {!loading && !hasSearched && trimmedInput.length === 0 && (
                  <Box
                    sx={{
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      color: isDarkMode ? "#94a3b8" : "#475569",
                    }}
                  >
                    <SearchIcon
                      sx={{
                        opacity: 0.7,
                        color: isDarkMode ? "#90caf9" : "#1976d2",
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Search by Name, Village, Phone Number, or Serial Number
                      (e.g., <strong>Ramesh</strong>, <strong>Rohna</strong>, or{" "}
                      <strong>5</strong>).
                    </Typography>
                  </Box>
                )}
              </ResultsPaper>
            )}
          </SearchContainer>
        </ClickAwayListener>
      )}
    </Fragment>
  );
}
